import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { Period } from '@spike/model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { useDrawerContext } from 'components/UI/V3/Drawer/DrawerContext';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import Button from 'components/UI/V3/Button/Button';
import { RadioCard } from 'components/EmailMarketing/UI/RadioCard/RadioCard';
import { FilterPill } from 'components/EmailMarketing/UI/FilterPill/FilterPill';
import { LocationsScopePicker } from 'components/EmailMarketing/UI/LocationsScopePicker/LocationsScopePicker';
import { EmailLocation } from 'components/EmailMarketing/types';
import { describeLocations } from 'components/EmailMarketing/utils';
import { CreateDataExport, DataExportResource } from 'model/dataExports';
import { DATA_EXPORT_RESOURCES, getResourceOption } from '../dataExportUtils';
import styles from './NewDataExportDrawer.module.scss';

type DatePresetId = 'all' | 'last30' | 'last90' | 'thisYear' | 'custom';

const DATE_PRESETS: Array<{ id: DatePresetId; label: string }> = [
    { id: 'all', label: 'All time' },
    { id: 'last30', label: 'Last 30 days' },
    { id: 'last90', label: 'Last 90 days' },
    { id: 'thisYear', label: 'This year' },
    { id: 'custom', label: 'Custom' }
];

const presetRange = (preset: DatePresetId, customRange: Period | null): Period | null => {
    const today = moment().startOf('day');

    switch (preset) {
        case 'last30':
            return { from: today.clone().subtract(29, 'days'), to: today };
        case 'last90':
            return { from: today.clone().subtract(89, 'days'), to: today };
        case 'thisYear':
            return { from: today.clone().startOf('year'), to: today };
        case 'custom':
            return customRange;
        default:
            return null;
    }
};

const formatRange = (range: Period | null): string => {
    if (!range) return 'All time';
    if (range.from.isSame(range.to, 'day')) return range.from.format('MMM D, YYYY');
    return `${range.from.format('MMM D, YYYY')} – ${range.to.format('MMM D, YYYY')}`;
};

interface NewDataExportDrawerProps {
    locations: Array<EmailLocation>;
    locationsLoading: boolean;
    creating: boolean;
    onSubmit: (dataExport: CreateDataExport) => Promise<boolean>;
    onClose: () => void;
}

// Lives inside <Drawer> so it can close it with the slide-out animation.
const NewDataExportForm: React.FC<Omit<NewDataExportDrawerProps, 'onClose'>> = ({
    locations,
    locationsLoading,
    creating,
    onSubmit
}) => {
    const { close } = useDrawerContext();

    const [resource, setResource] = useState<DataExportResource>('clients');
    // null = every location, so the selection stays "all" if locations change.
    const [locationIds, setLocationIds] = useState<Array<number> | null>(null);
    const [datePreset, setDatePreset] = useState<DatePresetId>('all');
    const [customRange, setCustomRange] = useState<Period | null>(null);

    const timeZone = useMemo(() => moment.tz.guess(), []);
    const range = presetRange(datePreset, customRange);
    const isAllLocations = locationIds === null || locationIds.length === locations.length;
    const selectedLocationIds = locationIds ?? locations.map(location => location.id);

    const locationsLabel = locationsLoading
        ? 'Loading locations…'
        : isAllLocations
        ? `All locations (${locations.length})`
        : describeLocations(selectedLocationIds, locations);

    const canSubmit = !creating && (datePreset !== 'custom' || customRange !== null);

    // Settings closes on ESC too; while the drawer is open ESC only closes the drawer.
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                close();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [close]);

    const submit = async () => {
        const created = await onSubmit({
            resource,
            locationIds: isAllLocations ? [] : selectedLocationIds,
            startDate: range ? range.from.format('YYYY-MM-DD') : null,
            endDate: range ? range.to.format('YYYY-MM-DD') : null
        });

        if (created) {
            close();
        }
    };

    return (
        <>
            <DrawerHeader
                sticky
                title="New export"
            />

            <DrawerBody className={styles.body}>
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>What do you want to export?</h3>
                    <div
                        className={styles.resources}
                        role="radiogroup"
                        aria-label="Data to export"
                    >
                        {DATA_EXPORT_RESOURCES.map(option => (
                            <RadioCard
                                key={option.id}
                                icon={option.icon}
                                title={option.label}
                                description={option.description}
                                checked={resource === option.id}
                                onSelect={() => setResource(option.id)}
                                className={styles.resourceCard}
                            />
                        ))}
                    </div>
                </section>

                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Locations</h3>
                    <FilterPill
                        size="large"
                        icon={faLocationDot}
                        label={locationsLabel}
                        className={styles.locationsPill}
                    >
                        {closePicker => (
                            <LocationsScopePicker
                                locations={locations}
                                selectedIds={selectedLocationIds}
                                onApply={ids => {
                                    setLocationIds(ids);
                                    closePicker();
                                }}
                            />
                        )}
                    </FilterPill>
                    <p className={styles.hint}>Each row includes the location it belongs to.</p>
                </section>

                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Date range</h3>
                    <div
                        className={styles.presets}
                        role="radiogroup"
                        aria-label="Date range"
                    >
                        {DATE_PRESETS.map(preset => (
                            <button
                                key={preset.id}
                                type="button"
                                role="radio"
                                aria-checked={datePreset === preset.id}
                                className={clsx(styles.preset, { [styles.presetSelected]: datePreset === preset.id })}
                                onClick={() => setDatePreset(preset.id)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>

                    {datePreset === 'custom' && (
                        <div className={styles.customRange}>
                            <div className={styles.customRangeValues}>
                                <div>
                                    <span className={styles.customRangeLabel}>From</span>
                                    <span className={styles.customRangeValue}>
                                        {customRange ? customRange.from.format('MMM D, YYYY') : '—'}
                                    </span>
                                </div>
                                <div>
                                    <span className={styles.customRangeLabel}>To</span>
                                    <span className={styles.customRangeValue}>
                                        {customRange ? customRange.to.format('MMM D, YYYY') : '—'}
                                    </span>
                                </div>
                            </div>
                            <DatePicker
                                rangeMode
                                timeZone={timeZone}
                                maxDate={moment()}
                                value={customRange}
                                onChange={setCustomRange}
                            />
                        </div>
                    )}

                    <p className={styles.hint}>Filters by the date each record was created.</p>
                </section>

                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Format</h3>
                    <div className={styles.format}>
                        <FontAwesomeIcon
                            icon={faFileCsv}
                            className={styles.formatIcon}
                        />
                        <div>
                            <p className={styles.formatTitle}>CSV</p>
                            <p className={styles.formatDescription}>Opens in Excel, Google Sheets and Numbers.</p>
                        </div>
                    </div>
                </section>
            </DrawerBody>

            <DrawerFooter className={styles.footer}>
                <div className={styles.summary}>
                    <span className={styles.summaryTitle}>{getResourceOption(resource).label}</span>
                    <span className={styles.summaryDetails}>
                        {isAllLocations ? 'All locations' : describeLocations(selectedLocationIds, locations)} ·{' '}
                        {datePreset === 'custom' && !customRange ? 'Pick a date range' : formatRange(range)}
                    </span>
                </div>
                <Button
                    size="large"
                    variant="black"
                    label="Start export"
                    isLoading={creating}
                    disabled={!canSubmit}
                    className={styles.submitButton}
                    onClick={submit}
                />
            </DrawerFooter>
        </>
    );
};

export const NewDataExportDrawer: React.FC<NewDataExportDrawerProps> = ({ onClose, ...formProps }) => (
    <Drawer onClose={onClose}>
        <NewDataExportForm {...formProps} />
    </Drawer>
);

export default NewDataExportDrawer;
